import { MantineProvider } from '@mantine/core';
import { RichTextEditor } from '@mantine/tiptap';
import {
  FormatQuote as BlockquoteIcon,
  FormatBold as BoldIcon,
  FormatListBulleted as BulletListIcon,
  Title as HeadingIcon,
  FormatItalic as ItalicIcon,
  FormatListNumbered as OrderedListIcon,
  Redo as RedoIcon,
  FormatUnderlined as UnderlineIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { Divider, IconButton, Paper, Stack, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Editor, generateJSON } from '@tiptap/core';
import { Table } from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import { TextAlign } from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect, useRef, useState } from 'react';
import TurndownService from 'turndown';

import { extractBodyContent, getHtmlContent, isHtml } from '../../utils/markdown-utils';
import '../markdown/table.css';

type ReportEditorProps = {
  height?: string;
  value: string;
  onChange?: (value: string) => void;
  isStreaming?: boolean;
  isEditing?: boolean;
  setIsEditing?: (isEditing: boolean) => void;
  disabled?: boolean;
  isReadOnly?: boolean;
};

export const ReportEditor = ({
  height = '55vh',
  value,
  onChange,
  isStreaming = false,
  isEditing = false,
  setIsEditing,
  disabled = false,
  isReadOnly = false,
}: ReportEditorProps) => {
  const theme = useTheme();
  const editorRef = useRef<HTMLDivElement | null>(null);
  const initialized = useRef(false);
  const [error, setError] = useState<Error | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
        bulletList: {},
        orderedList: {},
        listItem: {},
        codeBlock: {},
        blockquote: {},
        horizontalRule: {},
        code: {},
      }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: value,
    editable: !isStreaming && isEditing && !disabled,
    // onUpdate is not working as expected, so using onTransaction instead
    // onUpdate: ({ editor }) => {
    //   if (isEditing && !isStreaming) {
    //     try {
    //       onChange(editor.getHTML());
    //     } catch (err) {
    //       setError(err as Error);
    //     }
    //   }
    // },
    onTransaction({ editor, transaction }) {
      if (isEditing && !isStreaming) {
        try {
          onChange(editor.getHTML());
        } catch (err) {
          setError(err as Error);
        }
      }
    },
    onCreate: ({ editor }) => {
      try {
        initialized.current = true;
      } catch (err) {
        setError(err as Error);
      }
    },
    onDestroy: () => {
      initialized.current = false;
    },
  });

  useEffect(() => {
    if (!editor) return;

    try {
      editor.setEditable(!isStreaming && isEditing && !disabled);
    } catch (err) {
      setError(err as Error);
    }
  }, [isStreaming, isEditing, editor, disabled]);

  useEffect(() => {
    if (!editor || isEditing) return;
    try {
      const currentHtml = editor.getHTML();
      let content = value || '';

      if (content && extractBodyContent(currentHtml) !== extractBodyContent(content)) {
        if (!isHtml(content)) {
          content = getHtmlContent(content);
        }
        content = extractBodyContent(content);
        const json = generateJSON(content, editor.extensionManager.extensions);
        editor.commands.setContent(json, false);
      }
    } catch (err) {
      setError(err as Error);
    }
  }, [value, isEditing, editor]);

  if (error) {
    return null;
  }

  if (!editor) {
    return null;
  }

  // useEffect(() => {
  //   const handleClickOutside = (event: MouseEvent) => {
  //     if (editorRef.current && !editorRef.current.contains(event.target as Node) && isEditing) {
  //       setIsEditing && setIsEditing(false);
  //     }
  //   };
  //   document.addEventListener('mousedown', handleClickOutside);
  //   return () => {
  //     document.removeEventListener('mousedown', handleClickOutside);
  //   };
  // }, [isEditing]);

  return (
    <Paper
      elevation={3}
      sx={{
        borderRadius: 0,
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        height: '100%',
        boxShadow: 'none',
      }}
      onDoubleClick={() => !isReadOnly && setIsEditing && setIsEditing(true)}
      ref={editorRef}
    >
      <MantineProvider>
        <Stack
          m="xl"
          className="report-editor-container"
          p={2}
          height={height}
          sx={{
            overflow: 'auto',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          }}
        >
          <RichTextEditor
            editor={editor as Editor}
            styles={{
              toolbar: {
                padding: '8px',
                '& .mantine-RichTextEditor-control': {
                  width: 40,
                  height: 40,
                  padding: '8px',
                },
                '& .mantine-RichTextEditor-controlsGroup': {
                  gap: '4px',
                },
              },
              content: {
                minHeight: '200px',
                '& .ProseMirror': {
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.background.paper,
                  lineHeight: 1.45,
                },
                '& .ProseMirror h1': {
                  margin: '0 0 12px',
                  fontSize: '1.6rem',
                  lineHeight: 1.25,
                },
                '& .ProseMirror h2': {
                  margin: '20px 0 8px',
                  fontSize: '1.25rem',
                  lineHeight: 1.3,
                },
                '& .ProseMirror h3': {
                  margin: '14px 0 6px',
                  fontSize: '1.05rem',
                  lineHeight: 1.3,
                },
                '& .ProseMirror p': {
                  margin: '0 0 8px',
                },
                '& .ProseMirror ul, & .ProseMirror ol': {
                  margin: '4px 0 10px',
                  paddingLeft: '1.35rem',
                },
                '& .ProseMirror li': {
                  margin: '2px 0',
                  paddingLeft: '0.15rem',
                },
                '& .ProseMirror li p': {
                  margin: 0,
                },
                '& .ProseMirror li > ul, & .ProseMirror li > ol': {
                  margin: '2px 0 4px',
                },
                '& .ProseMirror hr': {
                  margin: '18px 0',
                  border: 0,
                  borderTop: `1px solid ${theme.palette.divider}`,
                },
              },
            }}
          >
            {isEditing && !isStreaming && (
              <RichTextEditor.Toolbar
                sticky
                style={{
                  backgroundColor: `${theme.palette.grey[100]}`,
                  borderRadius: '8px',
                  marginBottom: '8px',
                  position: 'sticky',
                  top: '0px',
                  zIndex: 1,
                }}
              >
                <RichTextEditor.ControlsGroup>
                  <Tooltip title="Bold">
                    <IconButton
                      onClick={() => editor?.chain().focus().toggleBold().run()}
                      size="small"
                      sx={{
                        width: 40,
                        height: 40,
                        color: editor?.isActive('bold') ? 'primary.main' : 'text.secondary',
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                    >
                      <BoldIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Italic">
                    <IconButton
                      onClick={() => editor?.chain().focus().toggleItalic().run()}
                      size="small"
                      sx={{
                        width: 40,
                        height: 40,
                        color: editor?.isActive('italic') ? 'primary.main' : 'text.secondary',
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                    >
                      <ItalicIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Underline">
                    <IconButton
                      onClick={() => editor?.chain().focus().toggleUnderline().run()}
                      size="small"
                      sx={{
                        width: 40,
                        height: 40,
                        color: editor?.isActive('underline') ? 'primary.main' : 'text.secondary',
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                    >
                      <UnderlineIcon />
                    </IconButton>
                  </Tooltip>
                </RichTextEditor.ControlsGroup>

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                <RichTextEditor.ControlsGroup>
                  <Tooltip title="Heading 1">
                    <IconButton
                      onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
                      size="small"
                      sx={{
                        width: 40,
                        height: 40,
                        color: editor?.isActive('heading', { level: 1 }) ? 'primary.main' : 'text.secondary',
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                    >
                      <HeadingIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Heading 2">
                    <IconButton
                      onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
                      size="small"
                      sx={{
                        width: 40,
                        height: 40,
                        color: editor?.isActive('heading', { level: 2 }) ? 'primary.main' : 'text.secondary',
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                    >
                      <HeadingIcon sx={{ fontSize: '0.9em' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Heading 3">
                    <IconButton
                      onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
                      size="small"
                      sx={{
                        width: 40,
                        height: 40,
                        color: editor?.isActive('heading', { level: 3 }) ? 'primary.main' : 'text.secondary',
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                    >
                      <HeadingIcon sx={{ fontSize: '0.8em' }} />
                    </IconButton>
                  </Tooltip>
                </RichTextEditor.ControlsGroup>

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                <RichTextEditor.ControlsGroup>
                  <Tooltip title="Blockquote">
                    <IconButton
                      onClick={() => editor?.chain().focus().toggleBlockquote().run()}
                      size="small"
                      sx={{
                        width: 40,
                        height: 40,
                        color: editor?.isActive('blockquote') ? 'primary.main' : 'text.secondary',
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                    >
                      <BlockquoteIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Bullet List">
                    <IconButton
                      onClick={() => editor?.chain().focus().toggleBulletList().run()}
                      size="small"
                      sx={{
                        width: 40,
                        height: 40,
                        color: editor?.isActive('bulletList') ? 'primary.main' : 'text.secondary',
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                    >
                      <BulletListIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Ordered List">
                    <IconButton
                      onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                      size="small"
                      sx={{
                        width: 40,
                        height: 40,
                        color: editor?.isActive('orderedList') ? 'primary.main' : 'text.secondary',
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                    >
                      <OrderedListIcon />
                    </IconButton>
                  </Tooltip>
                </RichTextEditor.ControlsGroup>

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                <RichTextEditor.ControlsGroup>
                  <Tooltip title="Undo">
                    <IconButton
                      onClick={() => editor?.chain().focus().undo().run()}
                      size="small"
                      disabled={!editor?.can().undo()}
                      sx={{
                        width: 40,
                        height: 40,
                        color: 'text.secondary',
                        '&:hover': { backgroundColor: 'action.hover' },
                        '&.Mui-disabled': { color: 'text.disabled' },
                      }}
                    >
                      <UndoIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Redo">
                    <IconButton
                      onClick={() => editor?.chain().focus().redo().run()}
                      size="small"
                      disabled={!editor?.can().redo()}
                      sx={{
                        width: 40,
                        height: 40,
                        color: 'text.secondary',
                        '&:hover': { backgroundColor: 'action.hover' },
                        '&.Mui-disabled': { color: 'text.disabled' },
                      }}
                    >
                      <RedoIcon />
                    </IconButton>
                  </Tooltip>
                </RichTextEditor.ControlsGroup>

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                <RichTextEditor.ControlsGroup>
                  <Tooltip title="Insert 3×3 Table">
                    <IconButton
                      onClick={() => editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
                      size="small"
                      sx={{ width: 40, height: 40, color: 'text.secondary', '&:hover': { backgroundColor: 'action.hover' } }}
                    >
                      {/* use any icon you prefer */}
                      <span style={{ fontSize: 12, fontWeight: 600 }}>Tbl</span>
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Add Column Before">
                    <IconButton onClick={() => editor?.chain().focus().addColumnBefore().run()} size="small" sx={{ width: 40, height: 40 }}>
                      <span style={{ fontSize: 12 }}>C−</span>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Add Column After">
                    <IconButton onClick={() => editor?.chain().focus().addColumnAfter().run()} size="small" sx={{ width: 40, height: 40 }}>
                      <span style={{ fontSize: 12 }}>C+</span>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Column">
                    <IconButton onClick={() => editor?.chain().focus().deleteColumn().run()} size="small" sx={{ width: 40, height: 40 }}>
                      <span style={{ fontSize: 12 }}>C×</span>
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Add Row Before">
                    <IconButton onClick={() => editor?.chain().focus().addRowBefore().run()} size="small" sx={{ width: 40, height: 40 }}>
                      <span style={{ fontSize: 12 }}>R−</span>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Add Row After">
                    <IconButton onClick={() => editor?.chain().focus().addRowAfter().run()} size="small" sx={{ width: 40, height: 40 }}>
                      <span style={{ fontSize: 12 }}>R+</span>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Row">
                    <IconButton onClick={() => editor?.chain().focus().deleteRow().run()} size="small" sx={{ width: 40, height: 40 }}>
                      <span style={{ fontSize: 12 }}>R×</span>
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Toggle Header Row">
                    <IconButton onClick={() => editor?.chain().focus().toggleHeaderRow().run()} size="small" sx={{ width: 40, height: 40 }}>
                      <span style={{ fontSize: 12 }}>Hdr</span>
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Merge Cells">
                    <IconButton onClick={() => editor?.chain().focus().mergeCells().run()} size="small" sx={{ width: 40, height: 40 }}>
                      <span style={{ fontSize: 12 }}>↔︎</span>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Split Cell">
                    <IconButton onClick={() => editor?.chain().focus().splitCell().run()} size="small" sx={{ width: 40, height: 40 }}>
                      <span style={{ fontSize: 12 }}>⇵</span>
                    </IconButton>
                  </Tooltip>
                </RichTextEditor.ControlsGroup>
              </RichTextEditor.Toolbar>
            )}

            <RichTextEditor.Content />
          </RichTextEditor>
        </Stack>
      </MantineProvider>
    </Paper>
  );
};

export const turndownServiceExtended = new TurndownService({ headingStyle: 'atx' });

// Add support for tables
turndownServiceExtended.addRule('table', {
  filter: 'table',
  replacement: function (_content, node) {
    const table = node as HTMLTableElement;
    const rows = Array.from(table.querySelectorAll('tr')).map((row) => {
      const cells = Array.from(row.children).map((cell) => {
        return (cell.textContent?.trim() || '').replace(/\|/g, '\\|');
      });

      return '| ' + cells.join(' | ') + ' |';
    });

    if (rows.length === 0) return '';

    // Create Markdown table with header separator
    const header = rows[0];
    const separator =
      '| ' +
      header
        .split('|')
        .slice(1, -1)
        .map(() => '---')
        .join(' | ') +
      ' |';
    return `\n\n${[header, separator, ...rows.slice(1)].join('\n')}\n\n`;
  },
});
